import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { getCurrentUserId, getAccessToken } from "../../../awsAuth";
import ListingDetails from "../../CreateListing/ListingDetails";
import "./Archive.scss";
import DynamicHeader from "../../Header/DynamicHeader";
import mascotArchive from "../img/cvantik-archive.png";
import listingIcon from "../img/listing.png";
import postIcon from "../img/post.png";
import moreIcon from "../img/more.png";
import unarchiveIcon from "../img/unarchive.png";
import deleteIcon from "../img/delete.png";
import PostCard from "../../Feed/Post/PostCard";
import { useNavigate } from "react-router-dom";

const DEFAULT_THUMB =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const Archive = () => {
  const [activeTab, setActiveTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postsHydrated, setPostsHydrated] = useState([]);
  const [loading, setLoading] = useState(true);

  const [enterWaveKey, setEnterWaveKey] = useState(0);
  const [modalListingId, setModalListingId] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [menuClosingFor, setMenuClosingFor] = useState(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState(null);
  const [menuSide, setMenuSide] = useState("right");

  const [menuNudgeX, setMenuNudgeX] = useState(0);
  const menuElRef = useRef(null);

  const userId = getCurrentUserId();
  const rootRef = useRef(null);
  const navigateHook = useNavigate();

  const safeNavigate = useCallback(
    (to, opts) => {
      if (typeof navigateHook === "function") {
        try {
          navigateHook(to, opts);
          return;
        } catch {}
      }
      if (typeof window !== "undefined" && typeof to === "string") {
        window.location.href = to;
      }
    },
    [navigateHook]
  );

  const keyFor = (type, id) => `${type}:${id}`;

  const withAuthorMeta = useCallback((p) => {
    const authorName =
      p.authorName ||
      p.author?.name ||
      p.userName ||
      p.username ||
      p.name ||
      p.profileName ||
      p.displayName ||
      p.user?.username ||
      p.user?.name ||
      p.ownerName ||
      p.owner?.name ||
      null;

    const authorAvatar =
      p.authorAvatar ||
      p.author?.avatar ||
      p.userAvatar ||
      p.avatar ||
      p.photoURL ||
      p.user?.avatar ||
      p.owner?.avatar ||
      null;

    const userIdCoerced =
      p.userId ||
      p.authorId ||
      p.ownerId ||
      p.user?.id ||
      p.owner?.id ||
      p.author?.id ||
      null;

    return {
      ...p,
      authorName: authorName || null,
      authorAvatar: authorAvatar || null,
      userId: userIdCoerced || p.userId || null,
    };
  }, []);

  const closeMenuSmooth = useCallback((key) => {
    if (!key) {
      setMenuOpenFor(null);
      setConfirmDeleteFor(null);
      setMenuClosingFor(null);
      setMenuNudgeX(0);
      return;
    }
    setMenuClosingFor(key);
    setTimeout(() => {
      setMenuClosingFor(null);
      setMenuOpenFor(null);
      setConfirmDeleteFor(null);
      setMenuNudgeX(0);
    }, 180);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpenFor) return;
      const target = e.target;
      if (target.closest(".card-menu") || target.closest(".card-more__btn"))
        return;
      closeMenuSmooth(menuOpenFor);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [menuOpenFor, closeMenuSmooth]);

  useEffect(() => {
    if (!menuOpenFor) {
      setMenuNudgeX(0);
      return;
    }

    setMenuNudgeX(0);

    const raf = requestAnimationFrame(() => {
      try {
        const el = menuElRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const vw =
          window.innerWidth || document.documentElement.clientWidth || 0;

        const PAD = 16;

        let nudge = 0;

        if (rect.left < PAD) {
          nudge = PAD - rect.left;
        }
        else if (rect.right > vw - PAD) {
          nudge = (vw - PAD) - rect.right;
        }

        if (nudge !== 0) setMenuNudgeX(nudge);
      } catch {}
    });

    return () => cancelAnimationFrame(raf);
  }, [menuOpenFor]);

  // Fetch archive
  useEffect(() => {
    let cancelled = false;

    const fetchArchive = async () => {
      if (!userId) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const token = await getAccessToken().catch(() => null);
        const res = await axios.get(`${API}/api/archive/${userId}`, {
          params: { _: Date.now() },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setListings(Array.isArray(res.data.listings) ? res.data.listings : []);
        const rawPosts = Array.isArray(res.data.posts) ? res.data.posts : [];
        setPosts(rawPosts);
      } catch (err) {
        console.error("❌ Помилка отримання архіву:", err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          requestAnimationFrame(() => setEnterWaveKey((k) => k + 1));
        }
      }
    };

    fetchArchive();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let ignore = false;

    const hydratePosts = async () => {
      if (!posts || posts.length === 0) {
        setPostsHydrated([]);
        return;
      }

      const normalized = posts.map(withAuthorMeta);

      const needFetch = normalized.filter(
        (p) => (!p.authorName || !p.authorAvatar) && p.userId
      );

      const fillDefaultsIfEmpty = (p) => ({
        ...p,
        authorName: p.authorName || "Користувач",
        authorAvatar: p.authorAvatar || DEFAULT_AVATAR,
      });

      if (needFetch.length === 0) {
        if (!ignore) setPostsHydrated(normalized.map(fillDefaultsIfEmpty));
        return;
      }

      try {
        const byId = new Map();
        await Promise.all(
          needFetch.map(async (p) => {
            try {
              const r = await axios.get(
                `${API}/api/account/${encodeURIComponent(p.userId)}`
              );
              const u = r?.data || {};
              byId.set(p.userId, {
                name: u.username || u.name || "Користувач",
                avatar: u.avatar || DEFAULT_AVATAR,
              });
            } catch {
              byId.set(p.userId, {
                name: "Користувач",
                avatar: DEFAULT_AVATAR,
              });
            }
          })
        );

        const enriched = normalized.map((p) => {
          if (
            (!p.authorName || !p.authorAvatar) &&
            p.userId &&
            byId.has(p.userId)
          ) {
            const u = byId.get(p.userId);
            return {
              ...p,
              authorName: p.authorName || u.name,
              authorAvatar: p.authorAvatar || u.avatar,
            };
          }
          return p;
        });

        if (!ignore) setPostsHydrated(enriched.map(fillDefaultsIfEmpty));
      } catch {
        if (!ignore) setPostsHydrated(normalized.map(fillDefaultsIfEmpty));
      }
    };

    hydratePosts();
    return () => {
      ignore = true;
    };
  }, [posts, withAuthorMeta]);

  // Actions
  const handleRestore = useCallback(async (type, id, createdAt) => {
  try {
    const token = await getAccessToken();

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };

    if (type === "listing") {
      await axios.patch(
        `${API}/api/listings/${id}/restore`,
        { createdAt, userId },
        config
      );

      setListings((prev) =>
        prev.filter((l) => String(l.listingId || l.id) !== String(id))
      );
    } else {
      await axios.patch(
        `${API}/api/posts/${id}/restore`,
        { createdAt, userId },
        config
      );

      setPosts((prev) =>
        prev.filter((p) => String(p.postId || p.id) !== String(id))
      );
    }
  } catch (err) {
    console.error("❌ Помилка відновлення:", err);
  }
}, [userId]);

  const handleDelete = useCallback(
    async (type, id, createdAt) => {
      try {
        const token = await getAccessToken();

        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: { createdAt, userId },
        };

        if (type === "listing") {
          await axios.delete(`${API}/api/listings/${id}`, config);
          setListings((prev) =>
            prev.filter((l) => (l.listingId || l.id) !== id)
          );
        } else {
          await axios.delete(`${API}/api/posts/${id}`, { data: { createdAt } });
          setPosts((prev) => prev.filter((p) => (p.postId || p.id) !== id));
        }
      } catch (err) {
        console.error("❌ Помилка видалення:", err);
      }
    },
    [userId]
  );

  return (
    <>
      <DynamicHeader
        title="Архів"
        variant="solid"
        size="regular"
        showBack
        withSpacer
      />
      <div className="archive" data-wave={enterWaveKey} ref={rootRef}>
        {/* */}
        <div
          className="archive__switch archive__switch--pills"
          role="tablist"
          aria-label="Тип об'єктів у архіві"
        >
          <button
            role="tab"
            aria-selected={activeTab === "listings"}
            className={`pill ${activeTab === "listings" ? "active" : ""}`}
            onClick={() => setActiveTab("listings")}
            title="Оголошення"
          >
            <img src={listingIcon} alt="" aria-hidden="true" />
            <span>Оголошення</span>
          </button>

          <button
            role="tab"
            aria-selected={activeTab === "posts"}
            className={`pill ${activeTab === "posts" ? "active" : ""}`}
            onClick={() => setActiveTab("posts")}
            title="Пости"
          >
            <img src={postIcon} alt="" aria-hidden="true" />
            <span>Пости</span>
          </button>
        </div>

        {/* */}
        <div className="archive__content">
          {/* */}
          {activeTab === "listings" && (
            <>
              {loading ? (
                <div className="archive__grid ig-grid is-loading">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      className="ig-card ig-card--skeleton"
                      style={{ "--i": i }}
                      key={`skl-l-${i}`}
                    >
                      <div className="skel skel__thumb" />
                      <div className="skel__caption">
                        <div className="skel skel__line skel__line--lg" />
                        <div className="skel skel__line skel__line--sm" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : listings.length === 0 ? (
                <div className="archive__empty-block">
                  <img
                    className="archive__mascot"
                    src={mascotArchive}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className="archive__empty-title">
                    Архів порожній… Зате порядок!
                  </div>
                  <div className="archive__empty-sub">
                    Ви можете сховати оголошення, коли воно тимчасово
                    неактуальне і повернути його в будь-який момент.
                  </div>
                </div>
              ) : (
                <div className="archive__grid ig-grid is-entering">
                  {listings.map((ad, idx) => {
                    const id = ad.listingId || ad.id;
                    const thumb =
                      ad.mainPhoto || ad.photos?.[0] || DEFAULT_THUMB;
                    const k = keyFor("listing", id);
                    const isMenuOpen = menuOpenFor === k;
                    const isMenuClosing = menuClosingFor === k;
                    const isConfirm = confirmDeleteFor === k;

                    return (
                      <div
                        key={id}
                        className={`archive__tile ig-card ${
                          isMenuOpen || isMenuClosing ? "is-menu-open" : ""
                        }`}
                        style={{ "--i": idx }}
                      >
                        {/* */}
                        <div className="card-more">
                          <button
                            className="card-more__btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isMenuOpen) {
                                closeMenuSmooth(k);
                              } else {
                                try {
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  const viewportWidth =
                                    window.innerWidth ||
                                    document.documentElement.clientWidth ||
                                    0;

                                  const spaceRight =
                                    viewportWidth - rect.right;

                                  const MENU_W = 240;

                                  const side =
                                    spaceRight < MENU_W ? "left" : "right";
                                  setMenuSide(side);
                                } catch {
                                  setMenuSide("right");
                                }
                                setMenuOpenFor(k);
                                setConfirmDeleteFor(null);
                              }
                            }}
                            aria-label="Дії"
                            title="Дії"
                          >
                            <img src={moreIcon} alt="" />
                          </button>

                          {(isMenuOpen || isMenuClosing) && (
                            <div
                              ref={menuElRef}
                              className={`card-menu card-menu--${menuSide} ${
                                isMenuOpen ? "menu-in" : ""
                              } ${isMenuClosing ? "menu-out" : ""}`}
                              role="menu"
                              onClick={(e) => e.stopPropagation()}
                              style={{ "--menu-nudge-x": `${menuNudgeX}px` }}
                            >
                              {!isConfirm ? (
                                <>
                                  <button
                                    className="card-menu__item card-menu__item--stagger1"
                                    onClick={() => {
                                      closeMenuSmooth(k);
                                      handleRestore(
                                        "listing",
                                        id,
                                        ad.createdAt
                                      );
                                    }}
                                  >
                                    <img src={unarchiveIcon} alt="" />
                                    <span>Повернути у видимість</span>
                                  </button>
                                  <button
                                    className="card-menu__item card-menu__item--stagger2"
                                    onClick={() => setConfirmDeleteFor(k)}
                                  >
                                    <img src={deleteIcon} alt="" />
                                    <span>Видалити</span>
                                  </button>
                                </>
                              ) : (
                                <div className="card-confirm">
                                  <div className="card-confirm__text">
                                    Точно видалити?
                                  </div>
                                  <div className="card-confirm__row">
                                    <button
                                      className="card-confirm__btn card-confirm__btn--ghost"
                                      onClick={() => setConfirmDeleteFor(null)}
                                    >
                                      Ні
                                    </button>
                                    <button
                                      className="card-confirm__btn card-confirm__btn--primary"
                                      onClick={() => {
                                        closeMenuSmooth(k);
                                        handleDelete(
                                          "listing",
                                          id,
                                          ad.createdAt
                                        );
                                      }}
                                    >
                                      Так
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* */}
                        <button
                          className="archive__thumb ig-thumb"
                          onClick={() => {
                            safeNavigate(`/listing/${id}`);
                            setModalListingId(id);
                          }}
                          aria-label={`Відкрити оголошення: ${
                            ad.title || "Оголошення"
                          }`}
                        >
                          <img
                            src={thumb}
                            alt={ad.title || "Оголошення"}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.currentTarget.src = DEFAULT_THUMB;
                            }}
                          />
                        </button>

                        {/* */}
                        <div className="archive__caption ig-caption">
                          <div
                            className="archive__title-line ig-title"
                            title={ad.title || "Без назви"}
                          >
                            {ad.title || "Без назви"}
                          </div>
                          {ad.description && (
                            <div className="archive__desc">
                              {ad.description.length > 100
                                ? ad.description.slice(0, 100) + "…"
                                : ad.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* */}
          {activeTab === "posts" && (
            <>
              {loading ? (
                <div className="archive__posts is-loading">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      className="archive__post-card archive__post-card--skeleton"
                      style={{ "--i": i }}
                      key={`skl-p-${i}`}
                    >
                      <div className="skel skel__head" />
                      <div className="skel skel__image" />
                      <div className="skel skel__line skel__line--lg" />
                      <div className="skel skel__line skel__line--sm" />
                      <div className="skel__actions">
                        <div className="skel skel__chip" />
                        <div className="skel skel__chip" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : postsHydrated.length === 0 ? (
                <div className="archive__empty-block">
                  <img
                    className="archive__mascot"
                    src={mascotArchive}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className="archive__empty-title">
                    Архів порожній… Зате порядок!
                  </div>
                  <div className="archive__empty-sub">
                    Ви можете приховати неактуальні пости і повернути
                    їх у видимість у будь-який час.
                  </div>
                </div>
              ) : (
                <div className="archive__posts is-entering">
                  {postsHydrated.map((post, idx) => {
                    const id = post.postId || post.id;
                    const k = keyFor("post", id);
                    const isMenuOpen = menuOpenFor === k;
                    const isMenuClosing = menuClosingFor === k;
                    const isConfirm = confirmDeleteFor === k;

                    return (
                      <div
                        key={id}
                        className={`archive__post-wrap ${
                          isMenuOpen || isMenuClosing ? "is-menu-open" : ""
                        }`}
                        style={{ "--i": idx }}
                      >
                        {/* */}
                        <div className="card-more card-more--post">
                          <button
                            className="card-more__btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isMenuOpen) {
                                closeMenuSmooth(k);
                              } else {
                                setMenuSide("left");
                                setMenuOpenFor(k);
                                setConfirmDeleteFor(null);
                              }
                            }}
                            aria-label="Дії"
                            title="Дії"
                          >
                            <img src={moreIcon} alt="" />
                          </button>

                          {(isMenuOpen || isMenuClosing) && (
                            <div
                              ref={menuElRef}
                              className={`card-menu card-menu--${menuSide} ${
                                isMenuOpen ? "menu-in" : ""
                              } ${isMenuClosing ? "menu-out" : ""}`}
                              role="menu"
                              onClick={(e) => e.stopPropagation()}
                              style={{ "--menu-nudge-x": `${menuNudgeX}px` }}
                            >
                              {!isConfirm ? (
                                <>
                                  <button
                                    className="card-menu__item card-menu__item--stagger1"
                                    onClick={() => {
                                      closeMenuSmooth(k);
                                      handleRestore(
                                        "post",
                                        id,
                                        post.createdAt
                                      );
                                    }}
                                  >
                                    <img src={unarchiveIcon} alt="" />
                                    <span>Повернути у видимість</span>
                                  </button>
                                  <button
                                    className="card-menu__item card-menu__item--stagger2"
                                    onClick={() => setConfirmDeleteFor(k)}
                                  >
                                    <img src={deleteIcon} alt="" />
                                    <span>Видалити</span>
                                  </button>
                                </>
                              ) : (
                                <div className="card-confirm">
                                  <div className="card-confirm__text">
                                    Точно видалити?
                                  </div>
                                  <div className="card-confirm__row">
                                    <button
                                      className="card-confirm__btn card-confirm__btn--ghost"
                                      onClick={() => setConfirmDeleteFor(null)}
                                    >
                                      Ні
                                    </button>
                                    <button
                                      className="card-confirm__btn card-confirm__btn--primary"
                                      onClick={() => {
                                        closeMenuSmooth(k);
                                        handleDelete(
                                          "post",
                                          id,
                                          post.createdAt
                                        );
                                      }}
                                    >
                                      Так
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* */}
                        <PostCard
                          item={post}
                          ownerView={true}
                          navigate={safeNavigate}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* */}
        {modalListingId && (
          <ListingDetails
            listingId={modalListingId}
            isModal={true}
            onClose={() => {
              setModalListingId(null);
              try {
                if (window.history.length > 1) window.history.back();
              } catch {}
            }}
          />
        )}
      </div>
    </>
  );
};

export default Archive;
